import { useAppRewardConfiguration } from '@hooks/mutations/registry/onboarding/useAppRewardConfiguration';
import React from 'react';
import { AddOutlined, MoreVert } from '@mui/icons-material';
import { Box, Button, CircularProgress } from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { AppRewardConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/AppReward';
import { ChoiceDialog } from '../../components/ChoiceDialog';
import PartyDisplay from '../../components/PartyDisplay';
import { DataTable } from '../../components/Table/DataTable';
import { useParties } from '../../contexts/PartiesContext';
import TemplateContract from '../../utils/TemplateContract';
import { fmt } from '../../utils/common';
import { AppRewardConfigurationDetails } from './AppRewardConfigDetails';
import { CreateAppRewardConfigurationDialog } from './CreateAppRewardConfigurationDialog';
import { useCreateAppRewardActions } from './useCreateAppRewardActions';

type Row = TemplateContract<AppRewardConfiguration>;
const createHeaders: () => GridColDef<Row>[] = () => {
  return [
    {
      field: 'operator',
      headerName: 'operator',
      flex: 1,
      renderCell: (params) => <PartyDisplay party={params.row.payload.operator} />,
    },
    {
      field: 'provider',
      flex: 1,
      headerName: 'provider',
      renderCell: (params) => <PartyDisplay party={params.row.payload.provider} />,
    },
    {
      field: 'DSO',
      flex: 1,
      headerName: 'dso',
      renderCell: (params) => <PartyDisplay party={params.row.payload.details.dso} />,
    },
    {
      field: 'Beneficiary',
      flex: 1,
      headerName: 'Beneficiary',
      renderCell: (params) => (
        <PartyDisplay party={params.row.payload.details.operatorAppRewardBeneficiary.beneficiary} />
      ),
    },
    {
      field: 'Weight',
      headerName: 'weight',
      flex: 1,
      renderCell: (params) =>
        fmt(params.row.payload.details.operatorAppRewardBeneficiary.weight, 2),
    },
    {
      field: 'a',
      headerName: '',
      align: 'right',
      renderCell: () => <MoreVert />,
    },
  ];
};

export const AppRewardConfigurationsPage: React.FC = () => {
  const rewardConfigs = useAppRewardConfiguration();
  const { party, operator } = useParties();
  const isOperator = party === operator;

  const [open, setOpen] = React.useState(false);
  const onClick = () => {
    setOpen(true);
  };
  const onCloseDialog = () => {
    setOpen(false);
  };
  const [row, setRow] = React.useState<Row | undefined>();
  const onClose = () => {
    setRow(undefined);
  };
  const actions = useCreateAppRewardActions(onClose, row);

  const onRowClick = (r: GridRowParams<Row>) => {
    setRow(r.row);
  };
  if (rewardConfigs.isLoading) {
    return <CircularProgress />;
  }

  const cols = createHeaders();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {isOperator && (
        <Button
          sx={{ ml: 'auto' }}
          onClick={onClick}
          startIcon={<AddOutlined color="inherit" />}
          size="small"
        >
          App Reward Configuration
        </Button>
      )}
      <ChoiceDialog title="Details" actions={actions} open={!!row} onClose={onClose}>
        {row && <AppRewardConfigurationDetails row={row} />}
      </ChoiceDialog>
      <DataTable
        onRowClick={onRowClick}
        columns={cols}
        rows={rewardConfigs.data || []}
        rowKey={(r) => r.contractId}
      />
      {open && <CreateAppRewardConfigurationDialog open onClose={onCloseDialog} />}
    </Box>
  );
};
